/* =============================================================================
 * Para onde levar quem DIRIGE um clube.
 *
 * O usuario de clube nao abre o Futigol para ver o proprio cadastro: ele abre
 * para trabalhar a competicao em curso - inscrever atleta, montar time, resolver
 * transferencia, conferir o jogo de domingo. Por isso o destino preferido dele e'
 * a AREA DO CLUBE DENTRO DA COMPETICAO (/competicoes/clube-administracao), e nao
 * o painel do clube inteiro (/clubes/clube-administracao), que e' cadastro,
 * elenco e a escolha da competicao.
 *
 * O PAINEL DO CLUBE CONTINUA SENDO A QUEDA, e nao some: e' la' que a competicao
 * se escolhe, e sem uma escolhida a area nao tem contexto para montar. A guarda
 * `exigirCompeticao` faria o desvio sozinha (ver app/rotas.js), mas quem monta um
 * atalho precisa saber ANTES para onde ele vai - e' o que muda o texto de apoio de
 * "a competicao em uso" para "escolha uma competicao".
 *
 * MORA AQUI, E NAO EM CADA TELA, porque a mesma pergunta e' feita em dois
 * momentos que precisam responder igual: o pos-login (pages/usuario/entrar.js) e
 * a home (pages/home.js). Uma regra so', num lugar so'.
 * ========================================================================== */

import { sessao } from './sessao.js';
import { idCompeticaoAtual } from './competicao-atual.js';
import { paraInteiro, paraLista, paraTexto } from '@shared/lib/valores.js';

/**
 * Os clubes que a pessoa realmente ADMINISTRA - o vinculo de TAB_USUARIO_CLUBE
 * com PermiteAdministrar. Quem esta' vinculado sem a permissao acompanha o clube,
 * mas nao tem o que fazer nas telas de gestao (ver capacidades.js).
 *
 * @returns {{ idClube: number, clube: string }[]}
 */
export function clubesQueAdministra() {
    return paraLista(sessao.clubesVinculados)
        .filter((c) => c.permiteAdministrar !== false)
        .map((c) => ({ idClube: paraInteiro(c.idClube), clube: paraTexto(c.clube) }))
        .filter((c) => c.idClube > 0);
}

/**
 * A porta de gestao deste clube: a area dele na competicao escolhida.
 *
 * SEM COMPETICAO ESCOLHIDA A QUEDA E' A LISTA DE COMPETICOES - a mesma do item
 * "Competições" do menu, e nao o painel do clube. E' la' que o dirigente escolhe
 * COMO CLUBE: cada card da lista traz o botao da area do clube, entao escolher a
 * competicao e entrar na gestao dela sao o mesmo gesto (ver
 * pages/competicoes/lista.js). O painel do clube continua sendo o caminho para as
 * competicoes que a liga nao publicou no site - aquelas nao aparecem nesta lista.
 *
 * @returns {string|null} null quando o id nao vale.
 */
export function areaDoClube(idClube) {
    const id = paraInteiro(idClube);
    if (id <= 0) return null;
    return idCompeticaoAtual() > 0 ? `/competicoes/clube-administracao/${id}` : '/competicoes';
}

/**
 * Onde a pessoa cai depois de entrar, quando ela nao veio de lugar nenhum (o
 * `?volta=` da tela de login sempre ganha - ela estava indo a algum lugar).
 *
 * DIRIGENTE DE UM CLUBE SO' VAI DIRETO PARA A GESTAO: e' o que ele veio fazer, e
 * o perfil - a tela de "minha conta" - so' o obrigava a um toque a mais toda vez.
 * Com mais de um clube o perfil volta a ser o destino: e' la' que estao os
 * clubes, um a um, e escolher qual deles nao e' decisao do app.
 */
export function inicioDoUsuario() {
    if (!sessao.usuario) return '/';

    const clubes = clubesQueAdministra();
    return clubes.length === 1 ? areaDoClube(clubes[0].idClube) : '/usuario/perfil';
}
